import { FC, useRef } from 'react';
import { alpha, useTheme } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import DateRangeIcon from '@mui/icons-material/DateRange';
import RmdpDatePicker, {
  DateObject,
  DatePickerRef,
} from 'react-multi-date-picker';
import formatDate from 'utils/formatDate';
import { isMobile } from 'utils/isMobile';
import { DATE_CALENDAR_CLASS } from 'app/core/theme';
import { DateFieldType, useDateField } from './useDateField';

interface Props {
  type: DateFieldType;
  surface?: 'dark' | 'light';
}

const ISO_FORMAT = 'YYYY-MM-DD';
const WEEK_DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const toDateObject = (day?: string) =>
  day ? new DateObject({ date: day, format: ISO_FORMAT }) : undefined;

/**
 * Omits rmdp's `rmdp-mobile` class: in mobile mode rmdp waits for an OK tap,
 * while plain single mode closes the calendar as soon as a day is picked.
 */
const DatePicker: FC<Props> = ({ type, surface = 'light' }) => {
  const theme = useTheme();
  const field = useDateField(type);
  const pickerRef = useRef<DatePickerRef>();

  const disabled = !field.bounds;
  const isDark = surface === 'dark';
  const variant = isDark && !isMobile ? 'outlined' : 'standard';
  const inputColor = isDark ? theme.palette.primary.contrastText : undefined;
  const disabledColor = inputColor && alpha(inputColor, 0.5);
  const helperStyle = {
    dark: {
      info: { color: theme.palette.primary.contrastText },
      warning: { color: theme.palette.primary.contrastText, fontWeight: 600 },
    },
    light: {
      info: { color: theme.palette.text.secondary },
      warning: { color: theme.palette.warning.main },
    },
  }[surface][field.helper?.tone ?? 'info'];

  const clear = () => {
    field.select(null);
    pickerRef.current?.closeCalendar();
  };

  const endAdornment =
    field.value && !disabled ? (
      <IconButton
        aria-label={`Clear ${field.label}`}
        size="small"
        edge="end"
        onClick={clear}
        sx={{ color: inputColor }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    ) : variant === 'outlined' ? (
      <DateRangeIcon sx={{ color: disabled ? disabledColor : inputColor }} />
    ) : null;

  return (
    <RmdpDatePicker
      ref={pickerRef}
      className={DATE_CALENDAR_CLASS}
      containerStyle={{ width: '100%' }}
      value={field.value}
      format={ISO_FORMAT}
      onChange={date =>
        field.select(
          date instanceof DateObject ? date.format(ISO_FORMAT) : null,
        )
      }
      minDate={field.bounds?.min}
      maxDate={field.bounds?.max}
      currentDate={toDateObject(field.openTo)}
      mapDays={({ date }) =>
        field.isBlocked(date.format(ISO_FORMAT))
          ? { disabled: true }
          : undefined
      }
      disabled={disabled}
      onOpenPickNewDate={false}
      numberOfMonths={isMobile ? 1 : 2}
      weekDays={WEEK_DAYS}
      portal
      zIndex={theme.zIndex.modal + 1}
      render={(_, openCalendar) => (
        <TextField
          label={field.label}
          value={formatDate(field.value)}
          variant={variant}
          fullWidth
          disabled={disabled}
          error={field.error}
          helperText={field.helper?.text}
          onFocus={openCalendar}
          inputProps={{ readOnly: true }}
          InputProps={{ endAdornment }}
          FormHelperTextProps={{
            sx: {
              '&, &.Mui-disabled': helperStyle,
            },
          }}
          sx={{
            input: { color: inputColor, minWidth: 220 },
            label: { color: inputColor },
            '& label.Mui-focused': { color: inputColor },
            '& label.Mui-disabled': { color: disabledColor },
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: disabledColor,
            },
            '& .MuiInput-underline': {
              '&:before, &:after, &:hover:not(.Mui-disabled):before': {
                borderBottomColor: inputColor,
              },
              '&.Mui-disabled:before': {
                borderBottomColor: disabledColor,
                borderBottomStyle: 'solid',
              },
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset, &:hover fieldset, &.Mui-focused fieldset': {
                borderColor: inputColor,
              },
              '&.Mui-disabled fieldset': { borderColor: disabledColor },
            },
          }}
        />
      )}
    />
  );
};

export default DatePicker;
